(() => {
  const storageKey = 'muya:wishlist';

  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  };

  const updateButton = (button, selected) => {
    button.classList.toggle('is-active', selected);
    button.setAttribute('aria-pressed', String(selected));
    button.setAttribute('aria-label', selected ? 'Favorilerden çıkar' : 'Favorilere ekle');
  };

  const syncButtons = (root = document) => {
    const wishlist = readWishlist();
    root.querySelectorAll('[data-muya-wishlist]').forEach((button) => {
      updateButton(button, wishlist.some((item) => item.handle === button.dataset.productHandle));
    });
  };

  const syncDockHeight = () => {
    const dock = document.querySelector('.mobile-dock');
    if (!dock) return;

    const update = () => {
      const height = dock.getBoundingClientRect().height;
      if (height > 0) document.documentElement.style.setProperty('--mobile-dock-height', `${height.toFixed(1)}px`);
    };

    update();
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(dock);
    window.addEventListener('resize', update, { passive: true });
  };

  const localizeProductLabels = (root = document) => {
    if (!document.documentElement.lang.toLowerCase().startsWith('tr')) return;

    const optionLabels = new Map([
      ['Color', 'Renk'],
      ['Colour', 'Renk'],
      ['Body', 'Beden'],
      ['Size', 'Beden']
    ]);

    root.querySelectorAll('.product__info .form__label, .facet-topbar .details__summary > span').forEach((label) => {
      label.childNodes.forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        const match = node.textContent.match(/^(\s*)(Color|Colour|Body|Size)(\s*:?)\s*/i);
        if (!match) return;
        const source = match[2].charAt(0).toUpperCase() + match[2].slice(1).toLowerCase();
        const translated = optionLabels.get(source);
        if (translated) node.textContent = node.textContent.replace(match[0], `${match[1]}${translated}${match[3]} `);
      });
    });

    root.querySelectorAll('.quick-view').forEach((quickView) => {
      const walker = document.createTreeWalker(quickView, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);

      nodes.forEach((node) => {
        if (node.parentElement?.closest('script, style')) return;
        const source = node.textContent.trim();
        if (!source) return;

        const direct = new Map([
          ['Add to cart', 'Sepete Ekle'],
          ['Buy it now', 'Hemen Satın Al'],
          ['View full details', 'Tüm Ürün Detaylarını Gör'],
          ['Size chart', 'Beden Tablosu'],
          ['Share:', 'Paylaş:'],
          ['Need help?', 'Yardım Merkezi'],
          ['In stock, ready to ship', 'Stokta, gönderime hazır']
        ]);

        let translated = direct.get(source);
        const stockMatch = source.match(/^Hurry, only (\d+) items? left in stock!$/i);
        const reviewMatch = source.match(/^(\d+) reviews?$/i);
        if (stockMatch) translated = `Acele edin, stokta yalnızca ${stockMatch[1]} ürün kaldı!`;
        if (reviewMatch) translated = `${reviewMatch[1]} değerlendirme`;
        if (!translated) return;

        node.textContent = node.textContent.replace(source, translated);
      });
    });
  };

  const syncMuyaPlusOffset = () => {
    const header = document.querySelector('.header-section, .header-sticky');
    const offset = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    document.documentElement.style.setProperty('--muya-plus-offset', `${offset}px`);

    const menu = document.querySelector('.muya-plus-menu');
    const actions = menu?.closest('.header__buttons');
    if (menu && actions) {
      const actionBounds = actions.getBoundingClientRect();
      const top = Math.max(0, Math.round(actionBounds.top));
      const center = (actionBounds.left + actionBounds.right) / 2;
      const width = Math.min(340, window.innerWidth - 32);
      const right = Math.max(16, Math.min(window.innerWidth - width - 16, window.innerWidth - center - width / 2));
      menu.style.setProperty('--muya-plus-popover-top', `${top}px`);
      menu.style.setProperty('--muya-plus-popover-width', `${Math.round(width)}px`);
      menu.style.setProperty('--muya-plus-popover-right', `${Math.round(right)}px`);
      menu.style.setProperty('--muya-plus-popover-spacer', `${Math.max(0, offset - top)}px`);
    }
  };

  const bindMegaMenuDismissal = () => {
    const header = document.querySelector('header');
    if (!header || header.dataset.muyaMegaDismissal === 'true') return;

    const controller = new AbortController();
    const { signal } = controller;
    const menus = () => [...header.querySelectorAll('details.mega-menu-trigger[open], details.muya-plus-menu[open]')];
    let leaveTimer;
    const cancelClose = () => window.clearTimeout(leaveTimer);
    const closeMenus = (returnFocus = false, preferredMenu = null, openMenus = menus()) => {
      const focusMenu = returnFocus ? preferredMenu || openMenus.find((menu) => menu.contains(document.activeElement)) || openMenus[0] : null;
      openMenus.forEach((menu) => {
        // details-mega caches its state in `open`; the setter runs the theme's close
        // cleanup. On a not-yet-upgraded <details> it simply drops the attribute.
        menu.open = false;
        const trigger = menu.querySelector(':scope > summary');
        trigger?.setAttribute('aria-expanded', 'false');
      });
      focusMenu?.querySelector(':scope > summary')?.focus({ preventScroll: true });
    };

    header.dataset.muyaMegaDismissal = 'true';
    // Scrolling a care target under a stationary pointer can enter the sticky
    // header and reopen a hover menu. Only fresh pointer input releases the guard.
    const releaseHover = () => {
      header.querySelectorAll('[data-muya-suppress-hover]').forEach((menu) => menu.removeAttribute('data-muya-suppress-hover'));
    };
    header.addEventListener('pointermove', (event) => {
      if (event.movementX || event.movementY) {
        const menu = event.target.closest?.('details.mega-menu-trigger[data-muya-suppress-hover]');
        releaseHover();
        if (menu?.getAttribute('trigger') === 'hover' && event.target.closest('summary') === menu.querySelector(':scope > summary') && !window.theme?.config?.isTouch) menu.open = true;
      }
    });
    header.addEventListener('pointerdown', releaseHover);
    header.addEventListener('keydown', releaseHover);
    document.addEventListener('muya:information-open', () => {
      cancelClose();
      header.querySelectorAll('details.mega-menu-trigger').forEach((menu) => menu.setAttribute('data-muya-suppress-hover', ''));
      closeMenus(false);
    }, { signal });
    document.addEventListener('click', (event) => {
      if (!header.contains(event.target)) closeMenus(false);
    }, { capture: true, signal });
    const headerSection = header.closest('.header-section');
    const hiddenObserver = new MutationObserver(() => {
      if (!headerSection?.classList.contains('header-hidden')) return;
      const focusedMenu = menus().some((menu) => menu.contains(document.activeElement));
      if (focusedMenu) headerSection.classList.remove('header-hidden');
      closeMenus(focusedMenu);
    });
    if (headerSection) hiddenObserver.observe(headerSection, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(header)) return;
      cancelClose();
      hiddenObserver.disconnect();
      controller.abort();
    }, { signal });
    header.addEventListener('pointerenter', cancelClose);
    header.addEventListener('pointerover', (event) => {
      if (event.target.closest('.mega-menu-trigger > summary')) {
        const plusMenu = header.querySelector('.muya-plus-menu[open]');
        if (plusMenu) plusMenu.open = false;
      }
    });
    header.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      cancelClose();
      leaveTimer = window.setTimeout(() => closeMenus(false), 700);
    });
    let escapeTrigger;
    // Handle our menu's Escape before document-level accessibility key handlers
    // consume it and blur the trigger. Other dialogs and controls keep their keys.
    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') { escapeTrigger = null; return; }
      escapeTrigger = null;
      const unclaimedFocus = (element) => element === document.body || element === document.documentElement;
      if (unclaimedFocus(event.target) && unclaimedFocus(document.activeElement)) {
        // Hover does not move focus. Dismiss only our hover megas without taking
        // ownership of Escape or the focus of a different dialog/control.
        if (event.defaultPrevented || window.theme?.config?.isTouch ||
          document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]:not([hidden]):not([inert])')) return;
        const hoverMenus = menus().filter((item) => item.matches('details.mega-menu-trigger[trigger="hover"]'));
        if (!hoverMenus.length) return;
        cancelClose();
        hoverMenus.forEach((item) => item.setAttribute('data-muya-suppress-hover', ''));
        closeMenus(false, null, hoverMenus);
        return;
      }
      const menu = menus().find((item) => item.contains(event.target));
      if (!menu || event.target.closest?.('select, dialog[open], [role="dialog"], [aria-modal="true"]')) return;
      event.preventDefault();
      event.stopPropagation();
      cancelClose();
      menu.setAttribute('data-muya-suppress-hover', '');
      escapeTrigger = menu.querySelector(':scope > summary');
      closeMenus(true, menu);
    }, { capture: true, signal });
    window.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape' || !escapeTrigger) return;
      if (event.target === escapeTrigger) {
        event.preventDefault();
        event.stopPropagation();
      }
      escapeTrigger = null;
    }, { capture: true, signal });
  };

  const openCwill = (target = '#cwl-home') => {
    history.replaceState(null, '', `${location.pathname}${location.search}`);
    requestAnimationFrame(() => {
      location.hash = target;
    });
  };

  const formatMuyaMoney = (amountInCents) => {
    const locale = document.documentElement.lang?.toLowerCase().startsWith('tr') ? 'tr-TR' : 'en-US';
    const currency = window.Shopify?.currency?.active || 'TRY';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: Number(amountInCents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(Number(amountInCents) / 100);
  };

  const syncMuyaPoints = (root = document, selectedPrice = null) => {
    const scope = root?.querySelectorAll ? root : document;
    const panels = [];

    if (scope.matches?.('[data-muya-points-rate]')) panels.push(scope);
    panels.push(...scope.querySelectorAll('[data-muya-points-rate]'));

    panels.forEach((panel) => {
      if (panel.dataset.cwillManaged === 'true') return;

      const price = Number(selectedPrice ?? panel.dataset.muyaPointsPrice);
      const rate = Number(panel.dataset.muyaPointsRate || 5);
      const output = panel.querySelector('[data-muya-points-amount]');
      if (!output || !Number.isFinite(price) || !Number.isFinite(rate)) return;

      const amount = Math.round(price * rate / 100);
      panel.dataset.muyaPointsPrice = String(price);
      output.textContent = formatMuyaMoney(amount);
    });
  };

  // A colour chosen on the card must open the product page on that colour.
  const linkCardToVariant = (card, variantId) => {
    if (!/^\d+$/.test(variantId || '')) return;

    card.querySelectorAll('a[href]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;

      url.searchParams.set('variant', variantId);
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    });
  };

  const selectCardVariantPreview = (trigger) => {
    const card = trigger.closest('.product-card');
    const image = card?.querySelector('.muya-product-card-image');
    const src = trigger.dataset.muyaImageSrc;
    if (!card || !image || !src) return;

    const srcset = trigger.dataset.muyaImageSrcset;
    image.src = src;
    if (srcset) image.srcset = srcset;
    if (trigger.dataset.muyaImageAlt) image.alt = trigger.dataset.muyaImageAlt;

    card.dataset.muyaVariantSelected = trigger.dataset.muyaVariantId || 'true';
    card.querySelectorAll('[data-muya-variant-preview]').forEach((item) => {
      item.setAttribute('aria-pressed', String(item === trigger));
    });
    linkCardToVariant(card, trigger.dataset.muyaVariantId);
  };

  // Sliders can stop bubbling clicks while handling drag gestures. Capture the
  // variant choice first so every product rail gets the same image-swap logic.
  document.addEventListener('click', (event) => {
    const variantPreview = event.target.closest('[data-muya-variant-preview]');
    if (!variantPreview) return;

    event.preventDefault();
    event.stopPropagation();
    selectCardVariantPreview(variantPreview);
  }, true);

  // Facet panels are the theme's accordion-details, which cache their state in
  // `open`. Closing through that setter keeps it in step with the attribute, so
  // the next summary click reopens the panel instead of toggling a stale state.
  const closeFacetPanel = (details) => {
    if (details?.open) details.open = false;
  };

  // Desktop facets behave as true dropdowns: a click outside closes the open
  // panel, while a selection is closed by FacetForm before its async refresh.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('#FacetFiltersContainer details[data-filter][open]').forEach((details) => {
      if (!details.contains(event.target)) closeFacetPanel(details);
    });
  }, true);

  // Desktop filter panels open from their button's start edge. A panel that
  // would cross the viewport edge (Price and other end-of-row filters, wrapped
  // rows) is aligned to its button's end edge instead; see muya-custom.css.
  const facetPanelSelector = '.muya-ugg-filter-controls .collection__topbar #FacetFiltersContainer details[data-filter]';
  const facetPanelGutter = 24; // px, the 1.5rem side margin in the panel's max-width.
  const desktopFacets = window.matchMedia('(min-width: 1280px)');

  const viewportOverflow = (element) => {
    const { left, right } = element.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    return Math.max(0, right - (viewportWidth - facetPanelGutter), facetPanelGutter - left);
  };

  const alignFacetPanel = (details) => {
    delete details.dataset.muyaFacetAlign;
    const panel = details.querySelector(':scope > .details__content');
    if (!panel || !desktopFacets.matches || !details.hasAttribute('open')) return;

    const startAlignedOverflow = viewportOverflow(panel);
    if (startAlignedOverflow === 0) return;

    details.dataset.muyaFacetAlign = 'end';
    // Keep the default side if flipping would not reduce the overflow.
    if (viewportOverflow(panel) >= startAlignedOverflow) delete details.dataset.muyaFacetAlign;
  };

  const alignOpenFacetPanels = () => {
    document.querySelectorAll(`${facetPanelSelector}[open]`).forEach(alignFacetPanel);
  };

  // Capture sees the native toggle (also fired for panels rendered open) and
  // the theme's bubbling accordion toggle, which fires as the panel opens.
  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.(facetPanelSelector)) alignFacetPanel(event.target);
  }, true);
  document.addEventListener('DOMContentLoaded', alignOpenFacetPanels);
  document.addEventListener('collection:reloaded', alignOpenFacetPanels);
  window.addEventListener('resize', alignOpenFacetPanels, { passive: true });

  // Desktop sort (collection and search): the theme opens facet-sort on pointer
  // clicks only. Its header is a real button; keep aria-expanded in sync, let a
  // keyboard press close the open list, and return focus to the button after a
  // keyboard-driven sort re-renders the bar.
  let sortUsesKeyboard = false;
  let restoreSortFocus = false;
  const sortToggle = (root) => root?.querySelector('[data-muya-sort-toggle]');
  const hideSort = (sortBy) => sortBy.closest('facet-sort')?.hide?.();

  new MutationObserver((records) => {
    records.forEach(({ target }) => {
      if (!target.matches?.('.sort-by')) return;
      sortToggle(target)?.setAttribute('aria-expanded', String(target.hasAttribute('open')));
    });
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['open'], subtree: true });

  document.addEventListener('pointerdown', () => {
    sortUsesKeyboard = false;
    restoreSortFocus = false;
  }, true);

  document.addEventListener('click', (event) => {
    const sortBy = event.target.closest?.('[data-muya-sort-toggle]')?.closest('.sort-by[open]');
    // The native component only opens on header clicks; make the same trigger close it.
    if (!sortBy) return;
    event.stopPropagation();
    hideSort(sortBy);
  }, true);

  document.addEventListener('change', (event) => {
    if (sortUsesKeyboard && event.target.closest?.('#SortByContainer')) restoreSortFocus = true;
  }, true);

  document.addEventListener('collection:reloaded', () => {
    if (!restoreSortFocus) return;
    restoreSortFocus = false;
    sortToggle(document.getElementById('SortByContainer'))?.focus();
  });

  document.addEventListener('focusout', (event) => {
    const sortBy = event.target.closest?.('#SortByContainer .sort-by[open]');
    if (!sortBy || !sortUsesKeyboard || sortBy.contains(event.relatedTarget)) return;
    hideSort(sortBy);
  });

  document.addEventListener('keydown', (event) => {
    if (event.target.closest?.('#SortByContainer')) sortUsesKeyboard = true;
    if (event.key !== 'Escape') return;

    const sortBy = event.target.closest?.('#SortByContainer .sort-by[open]');
    if (sortBy) {
      event.preventDefault();
      hideSort(sortBy);
      sortToggle(sortBy)?.focus();
      return;
    }

    const facet = event.target.closest?.('.muya-ugg-filter-controls #FacetFiltersContainer details[data-filter][open]');
    if (!facet) return;
    event.preventDefault();
    closeFacetPanel(facet);
    facet.querySelector(':scope > summary')?.focus();
  });

  // The wishlist is a native modal dialog. A theme drawer (the mobile menu) holding its trigger must finish
  // closing first, so two layers never own focus at once; focus then returns to whatever opened that drawer.
  const openThemeLayer = (element) => {
    for (let node = element.parentElement; node; node = node.parentElement) {
      if (node.hasAttribute('open') && node.events?.afterHide && typeof node.hide === 'function') return node;
    }
    return null;
  };

  let wishlistReturnFocus = null;
  const openWishlist = (dialog, trigger) => {
    if (!dialog.dataset.muyaFocusReturn) {
      dialog.dataset.muyaFocusReturn = 'true';
      dialog.addEventListener('close', () => {
        const target = wishlistReturnFocus;
        wishlistReturnFocus = null;
        if (target?.isConnected) target.focus();
      });
    }

    const show = () => {
      if (!dialog.open) dialog.showModal();
    };
    const layer = openThemeLayer(trigger);
    if (!layer) {
      wishlistReturnFocus = trigger;
      show();
      return;
    }

    wishlistReturnFocus = layer.activeElement || null;
    // hide() resolves on the drawer's own afterHide event, i.e. when its transition has really ended.
    Promise.resolve(layer.hide()).then(show);
  };

  document.addEventListener('click', (event) => {
    const wishlistOpen = event.target.closest('[data-muya-open-wishlist]');
    if (wishlistOpen) {
      const dialog = document.getElementById('MuyaWishlistDialog');
      const content = dialog?.querySelector('[data-muya-wishlist-content]');
      if (!dialog || !content) return;

      content.replaceChildren();
      const items = readWishlist();
      if (items.length === 0) {
        const empty = document.createElement('p');
        empty.textContent = 'Henüz favori ürününüz yok.';
        content.append(empty);
      } else {
        items.forEach(({ handle, title }) => {
          if (!handle) return;
          const link = document.createElement('a');
          link.href = `/products/${encodeURIComponent(handle)}`;
          link.textContent = title || handle;
          content.append(link);
        });
      }

      openWishlist(dialog, wishlistOpen);
      return;
    }

    if (event.target.closest('[data-muya-close-wishlist]')) {
      document.getElementById('MuyaWishlistDialog')?.close();
      return;
    }

    const cwillHandoff = event.target.closest('[data-cwill-handoff]');
    if (cwillHandoff) {
      event.preventDefault();
      const drawer = document.getElementById('MuyaPlusDrawer');
      const menu = cwillHandoff.closest('.muya-plus-menu');
      const target = cwillHandoff.dataset.cwillTarget || '#cwl-home';
      if (menu) menu.open = false;
      if (typeof drawer?.hide === 'function') drawer.hide();
      window.setTimeout(() => openCwill(target), 260);
      return;
    }

    const loyaltyLink = event.target.closest('[data-cwill-link], a[href^="#cwl-"]');
    if (loyaltyLink) {
      const target = loyaltyLink.getAttribute('href') || '#cwl-home';
      event.preventDefault();
      openCwill(target);
      return;
    }

    const button = event.target.closest('[data-muya-wishlist]');
    if (!button) return;

    const handle = button.dataset.productHandle;
    const title = button.dataset.productTitle || '';
    let wishlist = readWishlist();
    const selected = wishlist.some((item) => item.handle === handle);

    wishlist = selected
      ? wishlist.filter((item) => item.handle !== handle)
      : [...wishlist, { handle, title }];

    writeWishlist(wishlist);
    document.querySelectorAll('[data-muya-wishlist]').forEach((item) => {
      if (item.dataset.productHandle === handle) updateButton(item, !selected);
    });
    document.dispatchEvent(new CustomEvent('muya:wishlist:change', {
      detail: { handle, selected: !selected, items: wishlist }
    }));
  });

  document.addEventListener('click', (event) => {
    const option = event.target.closest?.('[data-muya-price-band]');
    if (!option) return;
    const group = option.closest('[data-muya-price-bands]');
    const minInput = group?.querySelector('[data-muya-price-min]');
    const maxInput = group?.querySelector('[data-muya-price-max]');
    if (!minInput || !maxInput) return;

    const clear = option.getAttribute('aria-pressed') === 'true';
    group.querySelectorAll('[data-muya-price-band]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === option && !clear));
    });
    minInput.value = clear ? '' : option.dataset.min;
    maxInput.value = clear ? '' : option.dataset.max;
    if (window.matchMedia('(min-width: 1280px)').matches) {
      const panel = option.closest('#FacetFiltersContainer details[data-filter]');
      closeFacetPanel(panel);
      // The pressed option is hidden with its panel; keep focus on the filter's own button.
      panel?.querySelector(':scope > summary')?.focus({ preventScroll: true });
    }
    maxInput.dispatchEvent(new Event('change', { bubbles: true }));
  });

  const initProductSummaries = (root = document) => {
    root.querySelectorAll('[data-muya-product-summary]:not(.muya-product-summary--fixed)').forEach((bar) => {
      if (bar.dataset.muyaSummaryReady) return;
      bar.dataset.muyaSummaryReady = 'true';

      const section = bar.closest('.section');
      const priceSource = document.getElementById(bar.dataset.priceSource);
      const skuSource = document.getElementById(bar.dataset.skuSource);
      const fixedBar = bar.dataset.sticky === 'true' ? bar.cloneNode(true) : null;
      if (fixedBar) {
        fixedBar.classList.add('muya-product-summary--fixed');
        fixedBar.removeAttribute('data-muya-product-summary');
        fixedBar.setAttribute('aria-hidden', 'true');
        const heading = fixedBar.querySelector('.muya-product-summary__title');
        if (heading) {
          const title = document.createElement('div');
          title.className = heading.className;
          title.append(...heading.childNodes);
          heading.replaceWith(title);
        }
        fixedBar.querySelector('.muya-product-summary__details')?.setAttribute('aria-hidden', 'true');
        document.body.appendChild(fixedBar);
      }

      const syncPrice = () => {
        const sku = skuSource?.querySelector('[data-muya-sku-value]')?.textContent?.trim();
        [bar, fixedBar].filter(Boolean).forEach((target) => {
          const slot = target.querySelector('[data-muya-summary-sku]');
          if (!slot) return;
          slot.hidden = !sku || skuSource.hidden;
          if (sku) {
            const label = document.createElement('strong');
            label.textContent = 'SKU:';
            slot.replaceChildren(label, document.createTextNode(` ${sku}`));
          } else slot.replaceChildren();
        });
        const livePrice = priceSource?.querySelector('.price');
        if (!livePrice) return;
        [bar, fixedBar].filter(Boolean).forEach((target) => {
          const slot = target.querySelector('[data-muya-summary-price]');
          if (!slot) return;
          slot.hidden = priceSource.hidden;
          slot.innerHTML = livePrice.outerHTML;
        });
      };
      syncPrice();
      const priceObserver = priceSource || skuSource ? new MutationObserver(syncPrice) : null;
      [priceSource, skuSource].filter(Boolean).forEach((source) => {
        priceObserver.observe(source, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true, characterData: true });
      });

      if (!fixedBar || !section) {
        const cleanupPrice = (event) => {
          if (!event.target.contains(bar)) return;
          priceObserver?.disconnect();
          document.removeEventListener('shopify:section:unload', cleanupPrice);
        };
        document.addEventListener('shopify:section:unload', cleanupPrice);
        return;
      }
      // Keep this query in step with the product summary media query in muya-custom.css.
      const mobileLayout = window.matchMedia('(max-width: 767px), (min-width: 768px) and (max-width: 1023px) and (max-height: 1199px)');
      let frame = 0;
      const updatePosition = () => {
        frame = 0;
        const header = document.querySelector('.header-sticky header[is="sticky-header"]');
        const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
        const barBottom = bar.getBoundingClientRect().bottom;
        const sectionBottom = section.getBoundingClientRect().bottom;
        const fixedHeight = fixedBar.offsetHeight;
        const shouldShow = mobileLayout.matches &&
          barBottom <= headerBottom &&
          sectionBottom > headerBottom + fixedHeight;
        fixedBar.style.setProperty('--muya-product-summary-top', `${headerBottom}px`);
        fixedBar.classList.toggle('is-visible', shouldShow);
      };
      const schedulePosition = () => {
        if (!frame) frame = requestAnimationFrame(updatePosition);
      };
      updatePosition();
      window.addEventListener('scroll', schedulePosition, { passive: true });
      window.addEventListener('resize', schedulePosition, { passive: true });
      mobileLayout.addEventListener?.('change', schedulePosition);
      const cleanupSummary = (event) => {
        if (!event.target.contains(bar)) return;
        window.removeEventListener('scroll', schedulePosition);
        window.removeEventListener('resize', schedulePosition);
        mobileLayout.removeEventListener?.('change', schedulePosition);
        cancelAnimationFrame(frame);
        priceObserver?.disconnect();
        fixedBar.remove();
        document.removeEventListener('shopify:section:unload', cleanupSummary);
      };
      document.addEventListener('shopify:section:unload', cleanupSummary);
    });
  };

  const revealActiveMobileSubcollection = (root = document) => {
    root.querySelectorAll('.muya-category-links').forEach((nav) => {
      const active = nav.querySelector('[aria-current="page"]');
      if (!active) return;
      requestAnimationFrame(() => {
        if (!nav.clientWidth) return;
        const navRect = nav.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        if (activeRect.left >= navRect.left && activeRect.right <= navRect.right) return;
        nav.scrollLeft += activeRect.left - navRect.left - (nav.clientWidth - activeRect.width) / 2;
      });
    });
  };

  const initCartConfirmation = () => {
    const dialog = document.getElementById('MuyaCartConfirmation');
    if (!dialog || dialog.dataset.muyaReady) return;
    dialog.dataset.muyaReady = 'true';
    let returnFocus = null;

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      if (returnFocus?.isConnected) returnFocus.focus();
    });

    document.addEventListener('ajaxProduct:added', (event) => {
      const result = event.detail?.product;
      if (!result) return;
      const added = result.items?.at(-1) || result;
      const title = added.product_title || added.title || (document.documentElement.lang.startsWith('tr') ? 'Ürün' : 'Product');
      const variant = added.variant_title && added.variant_title !== 'Default Title' ? added.variant_title : '';
      const image = added.image || added.featured_image?.url || '';
      const quantity = Number(added.quantity) || 1;
      const imageNode = dialog.querySelector('[data-muya-cart-confirm-image]');
      const variantNode = dialog.querySelector('[data-muya-cart-confirm-variant]');
      dialog.querySelector('[data-muya-cart-confirm-title]').textContent = title;
      const quantityNode = dialog.querySelector('[data-muya-cart-confirm-quantity]');
      quantityNode.textContent = `${quantityNode.dataset.label}: ${quantity}`;
      variantNode.textContent = variant;
      variantNode.hidden = !variant;
      imageNode.hidden = !image;
      if (image) imageNode.src = image;
      returnFocus = document.activeElement;
      if (!dialog.open) dialog.showModal();
      dialog.querySelector('.muya-cart-confirmation__checkout')?.focus();
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initCartConfirmation();
    syncButtons();
    syncDockHeight();
    syncMuyaPlusOffset();
    bindMegaMenuDismissal();
    localizeProductLabels();
    syncMuyaPoints();
    initProductSummaries();
    revealActiveMobileSubcollection();
  });
  window.addEventListener('resize', () => {
    const menu = document.querySelector('.muya-plus-menu[open]');
    if (window.innerWidth < 1200 && menu) menu.open = false;
    syncMuyaPlusOffset();
  }, { passive: true });
  window.addEventListener('scroll', () => {
    const menu = document.querySelector('.muya-plus-menu[open]');
    if (!menu) return;
    const headerSection = menu.closest('.header-section');
    if (headerSection?.dataset.stickyType === 'on-scroll-up' && headerSection.classList.contains('header-hidden')) {
      menu.open = false;
    }
    else {
      syncMuyaPlusOffset();
    }
  }, { passive: true });
  document.addEventListener('click', (event) => {
    if (event.target.closest('.muya-plus-menu > summary')) syncMuyaPlusOffset();
  }, true);
  document.addEventListener('click', (event) => {
    const menu = document.querySelector('.header__icons--end .muya-plus-menu[open]');
    if (!menu) return;

    if (event.target.closest('.muya-plus-menu__close')) {
      event.preventDefault();
      menu.open = false;
      menu.querySelector(':scope > summary')?.focus();
      return;
    }

    const action = event.target.closest('.header__icons--end .header__buttons > a, .header__icons--end .header__buttons > button');
    if (action) menu.open = false;
  }, true);
  document.addEventListener('shopify:section:load', (event) => {
    syncButtons(event.target);
    bindMegaMenuDismissal();
    localizeProductLabels(event.target);
    syncMuyaPoints(event.target);
    initProductSummaries(event.target);
    revealActiveMobileSubcollection(event.target);
  });
  document.addEventListener('variant:change', (event) => {
    const root = event.target.closest?.('product-info, .quick-view') || document;
    syncMuyaPoints(root, event.detail?.variant?.price);
  }, true);
  document.addEventListener('product:loaded', (event) => {
    const root = event.target || document;
    localizeProductLabels(root);
    syncMuyaPoints(root);
  });
  document.addEventListener('quickview:loaded', () => {
    localizeProductLabels(document);
    syncMuyaPoints(document);
  });
})();
